import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Heart, Check, Loader2, Sparkles, Trophy } from "lucide-react";
import useSocialMeta from "../hooks/useSocialMeta";
import "./FoundingCircle.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (c) => `$${(c / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const MILESTONES = [
  { pct: 25, label: "Foundation Built", msg: "Core development is fully funded." },
  { pct: 50, label: "Halfway to Launch", msg: "Infrastructure and testing are well underway." },
  { pct: 75, label: "Final Stretch", msg: "We're preparing for public release." },
  { pct: 100, label: "Version 1.0 Launched", msg: "Thank you. Liturgical Intelligence™ is now launching to churches." },
];

export default function FoundingCircle() {
  useSocialMeta({
    title: "Become a Founding Supporter | Liturgical Intelligence™",
    description: "Help launch the world's first AI-powered institutional memory platform for churches. Join the Founding Circle and help bring Version 1.0 to life.",
    path: "/founding-circle",
  });
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [tier, setTier] = useState(null);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({ name: "", email: "", message: "", anonymous: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [thanks, setThanks] = useState(null);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/founding-circle`); setData(r.data); }
    catch (e) { console.error("Failed to load Founding Circle campaign:", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/founding-circle/status/${sid}`);
        if (r.data.paid) { setThanks(r.data.contribution); window.posthog?.capture?.("founding_contribution_success", { amount_cents: r.data.contribution?.amount_cents }); load(); }
      } catch (e) { console.error("Failed to verify contribution status:", e); }
      params.delete("session_id"); setParams(params, { replace: true });
    })();
  }, []); // eslint-disable-line

  const amountCents = tier != null ? data.tiers[tier].amount_cents : Math.round(parseFloat(custom || "0") * 100);

  const contribute = async () => {
    if (!amountCents || amountCents < 500) { setError("Please choose a tier or enter at least $5."); return; }
    setBusy(true); setError("");
    try {
      const { data: res } = await axios.post(`${API}/founding-circle/contribute`, {
        amount_cents: amountCents, name: form.name, email: form.email || null,
        message: form.message, tier: tier != null ? data.tiers[tier].label : "",
        anonymous: form.anonymous, origin: window.location.origin,
      });
      window.posthog?.capture?.("founding_checkout_started", { amount_cents: amountCents });
      window.location.href = res.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start your contribution. Please try again.");
      setBusy(false);
    }
  };

  if (!data) return <div className="fc-loading"><Loader2 className="spin" /></div>;

  if (thanks) return (
    <div className="fc fc-ty-page" data-testid="fc-thankyou-page">
      <div className="fc-ty-card">
        <div className="fc-ty-icon"><Check size={36} /></div>
        <div className="fc-eyebrow"><Sparkles size={15} /> The Founding Circle</div>
        <h1>Thank You for Becoming a Founding Supporter</h1>
        <p>Your generosity helps churches preserve their worship traditions and gives ministry leaders more time for ministry.</p>
        <p className="fc-ty-sub">We'll keep you updated as Version 1.0 launches.</p>
        <button className="fc-give-btn" onClick={() => navigate("/")} data-testid="fc-ty-home">Return Home</button>
      </div>
    </div>
  );

  return (
    <div className="fc" data-testid="founding-circle-page">
      <header className="fc-top">
        <button className="fc-brand" onClick={() => navigate("/")}>Liturgical Intelligence™</button>
        <button className="fc-login" onClick={() => navigate("/login")} data-testid="fc-login">Sign in</button>
      </header>

      {thanks && (
        <div className="fc-thanks" data-testid="fc-thanks">
          <Check size={18} /> Thank you{thanks.name && thanks.name !== "Anonymous Supporter" ? `, ${thanks.name}` : ""}! Your contribution to the Founding Circle is confirmed.
        </div>
      )}

      <section className="fc-hero">
        <div className="fc-eyebrow"><Sparkles size={15} /> {data.tagline}</div>
        <h1>{data.title}</h1>
        <p className="fc-blurb">{data.blurb}</p>

        <div className="fc-progress" data-testid="fc-progress">
          <div className="fc-progress-label">Launch Progress</div>
          <div className="fc-stats">
            <div className="fc-stat">
              <span className="fc-stat-num fc-stat-raised" data-testid="fc-raised">{usd(data.raised_cents)}</span>
              <span className="fc-stat-label">Raised</span>
            </div>
            <div className="fc-stat">
              <span className="fc-stat-num" data-testid="fc-goal">{usd(data.goal_cents)}</span>
              <span className="fc-stat-label">Goal</span>
            </div>
            <div className="fc-stat">
              <span className="fc-stat-num" data-testid="fc-supporters">{data.supporters_count}</span>
              <span className="fc-stat-label">Founding Supporter{data.supporters_count === 1 ? "" : "s"}</span>
            </div>
          </div>
          <div className="fc-bar"><div className="fc-bar-fill" style={{ width: `${data.percent}%` }} data-testid="fc-bar" /></div>
          <div className="fc-pct-line"><b>{data.percent}%</b> funded</div>
          <p className="fc-social">Every contribution—large or small—moves us closer to launch.</p>

          <div className="fc-milestones" data-testid="fc-milestones">
            <div className="fc-ms-head"><Sparkles size={14} /> Launch Milestones</div>
            {MILESTONES.map((m) => {
              const reached = data.percent >= m.pct;
              const isFinal = m.pct === 100;
              return (
                <div key={m.pct} className={`fc-milestone${reached ? " reached" : ""}`} data-testid={`fc-milestone-${m.pct}`}>
                  <span className="fc-ms-badge">
                    {reached ? (isFinal ? <Trophy size={15} /> : <Check size={15} />) : <span className="fc-ms-pct">{m.pct}%</span>}
                  </span>
                  <span className="fc-ms-body">
                    <span className="fc-ms-title">{reached && !isFinal ? `${m.pct}% · ` : (isFinal && reached ? "" : "")}{m.label}</span>
                    <span className="fc-ms-msg">{m.msg}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="fc-give" data-testid="fc-give">
        <h2>Join the Founding Circle</h2>
        <div className="fc-tiers">
          {data.tiers.map((t, i) => (
            <button key={t.label} className={`fc-tier${tier === i ? " active" : ""}`} onClick={() => { setTier(i); setCustom(""); }} data-testid={`fc-tier-${i}`}>
              <span className="fc-tier-amt">{usd(t.amount_cents)}+</span>
              <span className="fc-tier-label">{t.label}</span>
              {t.impact && <span className="fc-tier-impact">{t.impact}</span>}
              {t.perk && <span className="fc-tier-perk">{t.perk}</span>}
            </button>
          ))}
          <div className={`fc-tier fc-custom${tier === null && custom ? " active" : ""}`}>
            <span className="fc-tier-label">Custom amount</span>
            <div className="fc-custom-input">
              <span>$</span>
              <input type="number" min="5" placeholder="100" value={custom}
                onChange={(e) => { setCustom(e.target.value); setTier(null); }} data-testid="fc-custom-amount" />
            </div>
          </div>
        </div>

        <div className="fc-form">
          <div className="fc-form-row">
            <input placeholder="Your name (for recognition)" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="fc-name" />
            <input placeholder="Email (for your receipt)" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="fc-email" />
          </div>
          <textarea placeholder="Leave a message (optional)" value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="fc-message" />
          <label className="fc-anon">
            <input type="checkbox" checked={form.anonymous}
              onChange={(e) => setForm({ ...form, anonymous: e.target.checked })} data-testid="fc-anon" />
            Make my contribution anonymous
          </label>
          {error && <div className="fc-error" data-testid="fc-error">{error}</div>}
          <button className="fc-give-btn" onClick={contribute} disabled={busy} data-testid="fc-contribute-btn">
            {busy ? <><Loader2 size={16} className="spin" /> Redirecting…</> : <><Heart size={16} /> Contribute {amountCents >= 500 ? usd(amountCents) : ""}</>}
          </button>
          <ul className="fc-trust" data-testid="fc-trust">
            <li><Check size={14} /> Secure Stripe Checkout</li>
            <li><Check size={14} /> SSL Encrypted</li>
            <li><Check size={14} /> Contributions support product development</li>
            <li><Check size={14} /> Not an investment or equity offering</li>
          </ul>
        </div>
      </section>

      <section className="fc-why" data-testid="fc-why">
        <h2>Where Your Support Goes</h2>
        <ul className="fc-why-list">
          <li><Check size={16} /> Final software development</li>
          <li><Check size={16} /> Trademark registration</li>
          <li><Check size={16} /> Production infrastructure</li>
          <li><Check size={16} /> Security &amp; testing</li>
          <li><Check size={16} /> Public launch</li>
        </ul>
      </section>

      <section className="fc-founder" data-testid="fc-founder">
        <img className="fc-founder-photo" src="/brand/rh-monogram.png" alt="Rasaan Hakiem monogram" />
        <div className="fc-founder-text">
          <div className="fc-eyebrow">Meet the Founder</div>
          <h2>Rasaan Hakiem</h2>
          <p>Rasaan Hakiem has spent more than twenty years serving churches through sacred music and worship
          leadership. Liturgical Intelligence™ is the next chapter of that ministry—using technology to
          strengthen the Church without replacing its people.</p>
          <div className="fc-founder-roles" data-testid="fc-founder-roles">
            <span>Founder</span>
            <span>Church Musician</span>
            <span>Liturgical Consultant</span>
          </div>
        </div>
      </section>

      <section className="fc-wall" data-testid="fc-wall">
        <h2>Founding Circle Recognition</h2>
        <p className="fc-wall-sub">“The people who believed before Version 1.0.”</p>
        {data.supporters.length > 0 ? (
          <div className="fc-supporters">
            {data.supporters.map((s, i) => (
              <div className="fc-supporter" key={i} data-testid={`fc-supporter-${i}`}>
                <div className="fc-sup-head">
                  <span className="fc-sup-name">{s.name}</span>
                  {s.tier && <span className="fc-sup-tier">{s.tier}</span>}
                </div>
                {s.message && <p className="fc-sup-msg">“{s.message}”</p>}
              </div>
            ))}
          </div>
        ) : (
          <div className="fc-wall-empty" data-testid="fc-wall-empty">No Founding Circle members yet. Be the first.</div>
        )}
      </section>

      <section className="fc-thanks-final" data-testid="fc-thanks-final">
        <h2>Thank you.</h2>
        <p>Whether you contribute, share this project, or simply pray for its success, thank you for helping
        bring Liturgical Intelligence™ into the world.</p>
      </section>

      <footer className="fc-foot">
        <button onClick={() => navigate("/")}>← Back to Liturgical Intelligence™</button>
      </footer>
    </div>
  );
}
